import { GameIcon } from '@/shared/ui/game-icon';
import { woodPanel, copperButton, gameColors } from '@/shared/ui/game-theme';
import { useCallback, useEffect, useId, useRef, useState, type CSSProperties } from 'react';
import { BuffType, type ActiveBuff, type BuffChargeState } from '@/game/types';
import { BUFF_COOLDOWN_MS } from '@/network/buffMap';
import { isBuffQuotaExhausted } from '@/modules/game-domain/buff-quota';
import { BUFF_VISUAL, BUFF_ICON, buffUiStrings } from './buffMeta';
import type { HeroResponse } from '@/network/api';
import { createPaymentInvoice, type PaymentInvoice } from '@/network/hero.api';
import { getTelegramUserId, getTelegramWebApp } from '@/shared/telegram';
import { useT, t } from '@/i18n';

interface BuffBarProps {
  buffs: ActiveBuff[];
  cooldownEndsAt: Partial<Record<BuffType, number>>;
  buffCharges: Partial<Record<BuffType, BuffChargeState>>;
  buffsLocked?: boolean;
  nowMs: number;
  onActivate: (type: BuffType) => void;
  onPurchaseRefill?: (type: BuffType, invoiceUrl: string) => void;
  onHeroUpdated?: (hero: HeroResponse) => void;
}

interface BuffButtonProps {
  buff: ActiveBuff;
  charge: BuffChargeState | undefined;
  maxCharges: number | undefined;
  onActivate: () => void;
  onRefill?: (type: BuffType, invoiceUrl: string) => void;
  nowMs: number;
  buffsLocked?: boolean;
}

// ---- Tooltip ----

const LONG_PRESS_MS = 400;
const TOOLTIP_AUTO_HIDE_MS = 2500;

/** When false, the green refill CTA in the hover tooltip is hidden (purchase still reachable from tap flow if any). */
const SHOW_BUFF_REFILL_BUTTON_IN_TOOLTIP = false;

const tooltipStyle: CSSProperties = {
  ...woodPanel,
  position: 'absolute',
  bottom: '110%',
  left: '50%',
  transform: 'translateX(-50%)',
  padding: '6px 10px',
  borderRadius: 8,
  backgroundColor: '#282421',
  border: '1px solid rgba(255,255,255,0.15)',
  color: gameColors.text,
  fontSize: 11,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
  zIndex: 100,
  textAlign: 'center',
  lineHeight: 1.4,
  boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
};

const tooltipArrow: CSSProperties = {
  position: 'absolute',
  bottom: -5,
  left: '50%',
  transform: 'translateX(-50%)',
  width: 0,
  height: 0,
  borderLeft: '5px solid transparent',
  borderRight: '5px solid transparent',
  borderTop: '5px solid rgba(10, 10, 20, 0.92)',
};

// ---- Charge Badge ----

const chargeBadgeBase: CSSProperties = {
  position: 'absolute',
  top: -4,
  right: -4,
  minWidth: 16,
  height: 16,
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 9,
  fontWeight: 700,
  lineHeight: 1,
  padding: '0 3px',
  zIndex: 10,
  pointerEvents: 'none',
  border: '1px solid rgba(0,0,0,0.4)',
};

// ---- Radial Cooldown SVG ----

function RadialCooldown({ progress }: { progress: number }) {
  if (progress <= 0) return null;

  const radius = 22;
  const cx = 25;
  const cy = 25;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - progress);

  return (
    <svg
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        transform: 'rotate(-90deg)',
        pointerEvents: 'none',
      }}
      viewBox="0 0 50 50"
    >
      <circle
        cx={cx}
        cy={cy}
        r={radius}
        fill="rgba(0,0,0,0.5)"
        stroke="none"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
      />
      <circle
        cx={cx}
        cy={cy}
        r={radius}
        fill="none"
        stroke="rgba(255,255,255,0.3)"
        strokeWidth={2}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
      />
    </svg>
  );
}

// ---- Helpers ----

function formatCountdown(seconds: number, units: { hourShort: string; minuteShort: string; secondShort: string }): string {
  if (seconds >= 3600) return `${Math.floor(seconds / 3600)}${units.hourShort}`;
  if (seconds > 300) return `${Math.floor(seconds / 60)}${units.minuteShort}`;
  return `${Math.ceil(seconds)}${units.secondShort}`;
}

// ---- Buff Button ----

const buttonBase: CSSProperties = {
  position: 'relative',
  border: '2px solid rgba(255,255,255,0.2)',
  backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  cursor: 'pointer',
  userSelect: 'none',
  WebkitTapHighlightColor: 'transparent',
};

function BuffButton({ buff, charge, maxCharges, onActivate, onRefill, nowMs, buffsLocked }: BuffButtonProps) {
  const tr = useT();
  const tooltipId = useId();
  const refillDialogId = useId();
  const visual = BUFF_VISUAL[buff.type];
  const { label, desc } = buffUiStrings(tr, buff.type);
  const meta = { ...visual, label, desc };
  const [pressed, setPressed] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showRefillConfirm, setShowRefillConfirm] = useState(false);
  const [invoice, setInvoice] = useState<PaymentInvoice | null>(null);
  const [invoiceUnavailable, setInvoiceUnavailable] = useState(false);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pressedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const didLongPress = useRef(false);

  const remainingEffectMs =
    buff.expiresAtMs != null ? Math.max(0, buff.expiresAtMs - nowMs) : buff.remainingMs;
  const isActive = remainingEffectMs > 0;

  const isOnCooldown = buff.cooldownRemainingMs > 0;
  const cooldownProgress =
    buff.cooldownMs > 0 ? buff.cooldownRemainingMs / buff.cooldownMs : 0;
  const cdSec = Math.ceil((buff.cooldownMs || 0) / 1000);

  // Charge state
  const remaining = charge?.remaining;
  const hasChargeData = remaining != null;
  const isOutOfCharges = isBuffQuotaExhausted(charge, nowMs);
  const isDisabled = isOnCooldown || (isOutOfCharges && !isActive) || !!buffsLocked;
  const canOpenRefill = isOutOfCharges && !isOnCooldown && !isActive && !buffsLocked;
  const periodEndMs = charge?.periodEnd ? Date.parse(charge.periodEnd) : NaN;
  const rechargeRemainingSec = Number.isFinite(periodEndMs) ? Math.max(0, Math.ceil((periodEndMs - nowMs) / 1000)) : null;
  const hasPendingRecharge = rechargeRemainingSec != null
    && rechargeRemainingSec > 0
    && (maxCharges == null || remaining == null || remaining < maxCharges);
  const countdownText = isOnCooldown
    ? formatCountdown(Math.ceil(buff.cooldownRemainingMs / 1000), tr)
    : isOutOfCharges && rechargeRemainingSec != null && rechargeRemainingSec > 0
      ? formatCountdown(rechargeRemainingSec, tr)
      : null;

  useEffect(() => {
    return () => {
      if (longPressTimer.current) clearTimeout(longPressTimer.current);
      if (pressedTimer.current) clearTimeout(pressedTimer.current);
      if (autoHideTimer.current) clearTimeout(autoHideTimer.current);
    };
  }, []);

  useEffect(() => {
    if (buffsLocked) {
      setShowTooltip(false);
    }
    if (buffsLocked || !isOutOfCharges) setShowRefillConfirm(false);
  }, [buffsLocked, isOutOfCharges]);

  useEffect(() => {
    if (!showRefillConfirm) return;
    let active = true;
    setInvoice(null);
    setInvoiceUnavailable(false);
    if (!getTelegramWebApp()?.openInvoice) {
      setInvoiceUnavailable(true);
      return;
    }
    createPaymentInvoice(buff.type === BuffType.Resurrection ? 'resurrection_refill' : 'buff_refill', buff.type, getTelegramUserId() ?? undefined)
      .then((quote) => {
        if (!active) return;
        if (quote.currency !== 'XTR' || !Number.isSafeInteger(quote.totalAmount) || quote.totalAmount <= 0 || !quote.invoiceUrl) {
          setInvoiceUnavailable(true);
          return;
        }
        setInvoice(quote);
      })
      .catch(() => { if (active) setInvoiceUnavailable(true); });
    return () => { active = false; };
  }, [showRefillConfirm, buff.type]);

  const openTooltip = (): void => {
    setShowTooltip(true);
    if (autoHideTimer.current) clearTimeout(autoHideTimer.current);
    autoHideTimer.current = setTimeout(() => setShowTooltip(false), TOOLTIP_AUTO_HIDE_MS);
  };

  const closeTooltip = (): void => {
    setShowTooltip(false);
    if (autoHideTimer.current) clearTimeout(autoHideTimer.current);
  };

  const handleTouchStart = (): void => {
    if (buffsLocked) return;
    didLongPress.current = false;
    longPressTimer.current = setTimeout(() => {
      didLongPress.current = true;
      openTooltip();
    }, LONG_PRESS_MS);
  };

  const handleTouchEnd = (): void => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleClick = (): void => {
    if (buffsLocked) return;
    if (didLongPress.current) {
      didLongPress.current = false;
      return;
    }
    setPressed(true);
    if (pressedTimer.current) clearTimeout(pressedTimer.current);
    pressedTimer.current = setTimeout(() => {
      pressedTimer.current = null;
      setPressed(false);
    }, 140);

    // When out of charges, show refill confirmation instead of doing nothing
    if (canOpenRefill) {
      setShowRefillConfirm(true);
      return;
    }

    if (isDisabled) return;
    onActivate();
  };

  const dimmedFaceOpacity = buffsLocked
    ? 0.4
    : isDisabled
      ? (isOutOfCharges && !isOnCooldown ? 0.3 : 0.55)
      : 1;

  const hitStyle: CSSProperties = {
    position: 'relative',
    width: '100%',
    height: 48,
    padding: 0,
    border: 'none',
    background: 'transparent',
    pointerEvents: buffsLocked ? 'none' : 'auto',
    cursor: isDisabled && !canOpenRefill ? 'not-allowed' : 'pointer',
    transform: pressed ? 'scale(0.94)' : 'scale(1)',
    transition: 'transform 80ms ease',
  };

  const activatorFaceStyle: CSSProperties = {
    ...buttonBase,
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    borderRadius: 10,
    borderColor: isActive ? meta.color : pressed ? '#fff' : 'rgba(255,255,255,0.2)',
    boxShadow: isActive
      ? `0 0 12px ${meta.color}`
      : pressed
        ? `0 0 10px rgba(255,255,255,0.5), inset 0 0 12px ${meta.color}66`
        : 'none',
    opacity: dimmedFaceOpacity,
    transition: 'opacity 150ms ease, box-shadow 80ms ease, border-color 80ms ease',
    pointerEvents: 'none',
  };

  return (
    <div className="th-buff-slot" style={{ position: 'relative', minWidth: 0, height: 48 }}>
      <button
        type="button"
        style={hitStyle}
        onClick={handleClick}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        onMouseEnter={buffsLocked ? undefined : openTooltip}
        onMouseLeave={buffsLocked ? undefined : closeTooltip}
        onFocus={buffsLocked ? undefined : openTooltip}
        onBlur={closeTooltip}
        aria-disabled={isDisabled && !canOpenRefill}
        aria-describedby={showTooltip ? tooltipId : undefined}
        aria-haspopup={canOpenRefill ? 'dialog' : undefined}
        aria-expanded={canOpenRefill ? showRefillConfirm : undefined}
        aria-controls={showRefillConfirm ? refillDialogId : undefined}
        aria-label={`${meta.label}: ${meta.desc}`}
      >
        {/* Dim only the activator (chrome + icon); tooltip/dialog are siblings with independent events. */}
        <div style={{ ...activatorFaceStyle, color: meta.color }} className="th-buff-activator" data-active={isActive}>
          {/* Inner wrapper clips cooldown overlay to button bounds */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              overflow: 'hidden',
              borderRadius: 8,
            }}
          >
            <RadialCooldown progress={cooldownProgress} />
          </div>
          <span className="th-buff-icon" style={{ position: 'relative' }}><GameIcon name={BUFF_ICON[buff.type]} size={22} /></span>

          <span className="th-buff-time" style={{ color: isOnCooldown ? gameColors.text : meta.color }}>
            {countdownText ?? (isActive ? `${Math.ceil(remainingEffectMs / 1000)}s` : '\u00a0')}
          </span>

          {/* Charge badge */}
          {hasChargeData && (
            <span
              style={{
                ...chargeBadgeBase,
                backgroundColor: isOutOfCharges ? '#6b3832' : '#493e30',
                color: isOutOfCharges ? '#e3aaa0' : gameColors.text,
              }}
            >
              {remaining}
            </span>
          )}
        </div>

      </button>

      {showTooltip && (
        <div id={tooltipId} role="tooltip" className="th-buff-tooltip" style={tooltipStyle}>
          <div style={{ color: meta.color, fontWeight: 700, marginBottom: 2 }}>{meta.label}</div>
          <div style={{ color: '#ddd', fontWeight: 400 }}>{meta.desc}</div>
          {cdSec > 0 && !isOutOfCharges && <div style={{ color: '#999', fontSize: 10, marginTop: 2 }}>CD: {isOnCooldown ? formatCountdown(Math.ceil(buff.cooldownRemainingMs / 1000), tr) : formatCountdown(cdSec, tr)}</div>}
          {hasChargeData && (
            <div
              style={{
                fontSize: 10,
                marginTop: 3,
                color: isOutOfCharges ? '#ff8844' : '#aaccff',
              }}
            >
              {tr.charges}: {remaining}/{maxCharges ?? '?'}
            </div>
          )}
          {hasPendingRecharge && (
            <div style={{ fontSize: 10, marginTop: 1, color: '#ff6644' }}>
              {tr.refillsAt} {formatCountdown(rechargeRemainingSec, tr)}
            </div>
          )}
          {SHOW_BUFF_REFILL_BUTTON_IN_TOOLTIP && isOutOfCharges && onRefill && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowRefillConfirm(true);
                closeTooltip();
              }}
              style={{
                marginTop: 4,
                padding: '3px 10px',
                fontSize: 10,
                fontWeight: 700,
                color: gameColors.text,
                backgroundColor: 'rgba(100, 180, 60, 0.85)',
                border: '1px solid rgba(100, 180, 60, 0.6)',
                borderRadius: 4,
                cursor: 'pointer',
                pointerEvents: 'auto',
              }}
            >
              {tr.refill}
            </button>
          )}
          <div style={tooltipArrow} />
        </div>
      )}

      {/* Refill confirmation popup */}
      {showRefillConfirm && (
        <div
          id={refillDialogId}
          role="dialog" className="th-refill-dialog"
          aria-label={t(tr.refillQuestion, { label: meta.label })}
          style={{
            position: 'absolute',
            bottom: '115%',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '8px 12px',
            borderRadius: 8,
            backgroundColor: '#282421',
            border: '1px solid #a17b51',
            color: gameColors.text,
            fontSize: 11,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            zIndex: 110,
            textAlign: 'center',
            boxShadow: '0 4px 16px rgba(0,0,0,0.6)',
          }}
        >
          <div style={{ marginBottom: 6 }}>
            {t(tr.refillQuestion, { label: meta.label })}
          </div>
          <div style={{ fontSize: 10, color: gameColors.muted, marginBottom: 8 }}>
            {invoiceUnavailable ? tr.paymentsUnavailable : invoice ? `${invoice.totalAmount} Stars (XTR)` : tr.loading}
          </div>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
            <button
              type="button"
              disabled={!invoice || invoiceUnavailable}
              onClick={(e) => {
                e.stopPropagation();
                setShowRefillConfirm(false);
                if (invoice) onRefill?.(buff.type, invoice.invoiceUrl);
              }}
              style={{
                padding: '4px 12px',
                fontSize: 10,
                fontWeight: 700,
                color: gameColors.text,
                backgroundColor: 'rgba(100, 180, 60, 0.85)',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                ...copperButton,
              }}
            >
              {tr.refill}
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowRefillConfirm(false);
              }}
              style={{
                padding: '4px 12px',
                fontSize: 10,
                fontWeight: 700,
                color: gameColors.muted,
                backgroundColor: 'rgba(255,255,255,0.08)',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                ...copperButton,
              }}
            >
              {tr.cancel}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Buff Bar ----

/** All buff types that should always be visible as buttons */
const ALL_BUFF_TYPES: BuffType[] = [
  BuffType.Heal, BuffType.Rage, BuffType.Shield,
  BuffType.Rush, BuffType.PowerPotion, BuffType.WarCry,
  BuffType.Luck,
];

function getBuffEntry(
  type: BuffType,
  activeBuffs: ActiveBuff[],
  cooldownEndsAt: Partial<Record<BuffType, number>>,
  nowMs: number,
): ActiveBuff {
  const active = activeBuffs.find((b) => b.type === type);
  const cdEnd = cooldownEndsAt[type];
  const cdRem = cdEnd != null ? Math.max(0, cdEnd - nowMs) : 0;
  const cdFull = active?.cooldownMs || BUFF_COOLDOWN_MS[type];

  if (active) {
    const exp = active.expiresAtMs;
    const remainingMs = exp != null ? Math.max(0, exp - nowMs) : active.remainingMs;
    return {
      ...active,
      remainingMs,
      cooldownMs: active.cooldownMs || cdFull,
      cooldownRemainingMs: cdRem,
    };
  }

  return {
    type,
    remainingMs: 0,
    durationMs: 0,
    cooldownMs: cdFull,
    cooldownRemainingMs: cdRem,
  };
}

export function BuffBar({
  buffs,
  cooldownEndsAt,
  buffCharges,
  buffsLocked,
  nowMs,
  onActivate,
  onPurchaseRefill,
}: BuffBarProps) {
  const handleActivate = useCallback(
    (type: BuffType) => () => onActivate(type),
    [onActivate],
  );

  const handleRefill = useCallback(
    (type: BuffType, invoiceUrl: string) => {
      onPurchaseRefill?.(type, invoiceUrl);
    },
    [onPurchaseRefill],
  );

  return (
    <div className="th-buff-strip">
      {ALL_BUFF_TYPES.map((type) => {
        const buff = getBuffEntry(type, buffs, cooldownEndsAt, nowMs);
        const charge = buffCharges[type];
        const maxCharges = charge?.max;
        return (
          <BuffButton
            key={type}
            buff={buff}
            charge={charge}
            maxCharges={maxCharges}
            onActivate={handleActivate(type)}
            onRefill={handleRefill}
            nowMs={nowMs}
            buffsLocked={buffsLocked}
          />
        );
      })}
    </div>
  );
}
